import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { answerBlock } from 'actions';

import LocalisedMessage from 'components/Common/LocalisedMessage';
import Question from 'components/Questionnaires/Question';
import { QuestionDropDownField, QuestionYesNoField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion, isRequired } from 'components/Questionnaires/validators';
import GetDictionaryArray from 'common/language/intlArrayHelper';
import showQuestion from 'components/Questionnaires/questions';

// Hook
function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

export const QuestionEntityTypeBlock = ({ block, onAnswer }) => {
  const dispatch = useDispatch();
  const { id, index, disabled } = block;
  const { formType } = useParams(window.location.href);

  const getValidCh4Combos = (validCh3Combos) => {
    const reversedObject = {};

    Object.entries(validCh3Combos).forEach(([key, array]) => {
      array.forEach((item) => {
        reversedObject[item] = reversedObject[item] ? [...reversedObject[item], key] : [key];
      });
    });

    return reversedObject;
  };

  const ch3Dictionary = GetDictionaryArray('questionnaire.ch3.items')[0] ?? [];
  const ch4Dictionary = GetDictionaryArray('questionnaire.ch4.items')[0] ?? [];
  const validCh3Combos = useSelector(state => state?.app?.onlineConfig?.validCh3Ch4Combinations[formType]) ?? [];
  const validCh4Combos = useMemo(() => getValidCh4Combos(validCh3Combos), [validCh3Combos]);

  const getStatuses = (dictionary, validCombos) => {
    const uniqueValues = Object.keys(validCombos);
    const result = uniqueValues.map((statusKey) => {
      const status = dictionary.find(item => item.value === statusKey);
      if (!status) {
        return null;
      }
      const { value, label, description } = status;
      return Object.assign({
        value,
        label,
        description
      });
    });

    return result;
  };

  const ch4Statuses = useMemo(() => getStatuses(ch4Dictionary, validCh4Combos), [ch4Dictionary]);
  const ch3Statuses = useMemo(() => getStatuses(ch3Dictionary, validCh3Combos), [ch3Dictionary]);

  const getValidStatuses = (answer, statuses, combos) => {
    if (!answer) {
      return statuses;
    }

    const validKeys = combos[answer];
    if (!validKeys) {
      return statuses;
    }

    const result = validKeys.map(key => statuses.find(element => element.value === key));
    return result;
  };

  const getValidCh3Statuses = ch4Answer => getValidStatuses(ch4Answer, ch3Statuses, validCh4Combos);
  const getValidCh4Statuses = ch3Answer => getValidStatuses(ch3Answer, ch4Statuses, validCh3Combos);

  const isComboValid = (ch3Answer, ch4Answer) => {
    if (!ch3Answer || !ch4Answer) {
      return false;
    }

    return validCh3Combos[ch3Answer].includes(ch4Answer);
  };

  const defaultCh3Option = useMemo(() => ch3Dictionary.find(item => item.value === ''), [ch3Dictionary]);
  const defaultCh4Option = useMemo(() => ch4Dictionary.find(item => item.value === ''), [ch4Dictionary]);

  const [answer, setAnswer] = useState(block?.answers ?? {});

  const defaultEntityTypeState = (questionId, defaultOption, allOptions) => ({
    id: questionId,
    component: QuestionDropDownField,
    componentProps: {
      items: [defaultOption, ...allOptions],
      preventTranslation: true
    },
    className: 'col-6',
    label: `questionnaire.entityType.${ questionId }.label`,
    visible: true,
    disabled: disabled ?? undefined,
    validation: { conditions: [{ condition: requiredValidator, message: `questionnaire.entityType.${ questionId }.errorMessage` }] },
    answer: answer[questionId]
  });

  const [ch3Status, setCh3Status] = useState(defaultEntityTypeState('ch3Status', defaultCh3Option, ch3Statuses));
  const [ch4Status, setCh4Status] = useState(defaultEntityTypeState('ch4Status', defaultCh4Option, ch4Statuses));
  const prevCh3Status = usePrevious(ch3Status);
  const prevCh4Status = usePrevious(ch4Status);

  const validHybridEntityCh3 = ['DisregardedEntity', 'Partnership', 'SimpleTrust', 'GrantorTrust'];

  const isHybridEntityVisible = ch3 => validHybridEntityCh3.includes(ch3);

  const [hybridEntity, setHybridEntity] = useState({
    id: 'hybridEntity',
    component: QuestionYesNoField,
    label: 'questionnaire.entityType.hybridEntity.label',
    disabled: block.disabled ?? undefined,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.entityType.hybridEntity.errorMessage' }
      ]
    },
    answer: answer?.dreName,
    visible: false
  });

  const blockIdentifier = useMemo(() => ((block && id) ? `${ id }${ index !== undefined ? (`.${ index }`) : '' }` : undefined), [index]);
  const qAnswers = useSelector(state => state?.questionnaires?.formQuestionnaire?.answers);

  const isQuestionValid = (question) => {
    if (!showQuestion(question, block?.answers, qAnswers)) {
      return true;
    }

    if (!isRequired(question)) {
      return !question.answer || question.isValid;
    }

    if (question.answer === undefined) {
      return undefined;
    }

    return question.isValid;
  };

  const isValid = () => {
    const hybridEntityValid = isQuestionValid(hybridEntity);
    const ch3Valid = isQuestionValid(ch3Status);
    const ch4Valid = isQuestionValid(ch4Status);

    return ch3Valid && ch4Valid && isComboValid(ch3Status?.answer, ch4Status?.answer) && hybridEntityValid;
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid()));
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  useEffect(() => {
    if (prevCh3Status?.answer !== ch3Status.answer) {
      setCh4Status({ ...ch4Status,
        componentProps: {
          items: [defaultCh4Option, ...getValidCh4Statuses(ch3Status.answer)],
          preventTranslation: true
        } });
    }
  }, [ch3Status]);

  useEffect(() => {
    if (prevCh4Status?.answer !== ch4Status.answer) {
      setCh3Status({ ...ch3Status,
        componentProps: {
          items: [defaultCh3Option, ...getValidCh3Statuses(ch4Status.answer)],
          preventTranslation: true
        } });
    }
  }, [ch4Status]);

  const onChapter3Change = (question, value) => {
    const newCh3Status = { ...question, answer: value };
    validateQuestion(newCh3Status);
    setCh3Status(newCh3Status);
    const newAnswer = { ch3Status: value };
    const hybridVisible = isHybridEntityVisible(value);
    const newHybridEntity = { visible: hybridVisible };
    if (!hybridVisible) {
      newHybridEntity.answer = undefined;
      newHybridEntity.isValid = undefined;
      newHybridEntity.errorMessages = undefined;
      newAnswer.hybridEntity = undefined;
    }
    setHybridEntity({ ...hybridEntity, ...newHybridEntity });

    setAnswer({ ...answer, ...newAnswer });
  };

  const onChapter4Change = (question, value) => {
    const newCh4Status = { ...question, answer: value };
    validateQuestion(newCh4Status);
    setCh4Status(newCh4Status);
    setAnswer({ ...answer, ch4Status: value });
  };

  const onHybridEntityChange = (question, value) => {
    const newHybridEntity = { ...question, answer: value };
    validateQuestion(newHybridEntity);
    setHybridEntity(newHybridEntity);
    setAnswer({ ...answer, hybridEntity: value });
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.ch3Status) {
        onChapter3Change({ ...ch3Status, isForceEdited: true }, '');
      }
      if (!answer?.ch4Status) {
        onChapter4Change({ ...ch4Status, isForceEdited: true }, '');
      }
      if (!answer?.hybridEntity) {
        onHybridEntityChange({ ...hybridEntity, isForceEdited: true }, '');
      }
    }
  }, [block.isForceEdited]);

  const getAnswerLabel = status => status.componentProps.items.find(element => element.value === status.answer).label ?? '';
  const getAnswerDescription = status => status.componentProps.items.find(element => element.value === status.answer).description ?? '';

  const showCertificationMessage = ch3Status?.answer || ch4Status?.answer;

  const renderSelectedEntityTypeDescription = status => (
    <React.Fragment>
      <div className="mt-2 question-card-header col-12">
        <h3 className="card-group"><LocalisedMessage id={ getAnswerLabel(status) } /></h3>
      </div>
      <div className="mt-2 col-12"><LocalisedMessage id={ getAnswerDescription(status) } /></div>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Question key={ ch3Status.id } blockId={ blockIdentifier } question={ ch3Status } { ...ch3Status.componentProps } onChange={ (q, a) => onChapter3Change(q, a) } />
      <Question key={ ch4Status.id } blockId={ blockIdentifier } question={ ch4Status } { ...ch4Status.componentProps } onChange={ (q, a) => onChapter4Change(q, a) } />
      { showCertificationMessage && <p className="mt-2 mb-0 col-12"><LocalisedMessage id="questionnaire.entityType.certification" /></p> }
      { ch3Status?.answer && renderSelectedEntityTypeDescription(ch3Status) }
      { ch4Status?.answer && renderSelectedEntityTypeDescription(ch4Status) }
      {showQuestion(hybridEntity, block?.answers)
      && <Question
        key={ hybridEntity.id }
        blockId={ blockIdentifier }
        question={ hybridEntity }
        { ...hybridEntity.componentProps }
        onChange={ (q, a) => onHybridEntityChange(q, a) }
      />}
    </React.Fragment>
  );
};

QuestionEntityTypeBlock.displayName = 'QuestionEntityTypeBlock';

QuestionEntityTypeBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func
};

QuestionEntityTypeBlock.defaultProps = {
  onAnswer: null,
};

export default QuestionEntityTypeBlock;
